import React, { useEffect, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import { UiButton } from '../../ui/ui-button';

import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

interface Props extends ReduxFormFieldProps<Array<Partial<JobFinalizingHireStep>>> {
  commonFinalizingHireSteps: Array<FinalizingHireStep>;
}

export const JobFinalizingHireStepsReduxFormField = (props: Props) => {
  const [finalizingHireSteps, setFinalizingHireSteps] = useState<
    Array<Partial<JobFinalizingHireStep>>
  >([]);
  const [customStepDescription, setCustomStepDescription] = useState<string>('');

  useEffect(() => {
    setFinalizingHireSteps(props.input.value);
  }, [props.input.value]);

  const onChange = (newFinalizingHireSteps: Array<Partial<JobFinalizingHireStep>>) => {
    setFinalizingHireSteps(newFinalizingHireSteps);
    setCustomStepDescription('');
    props.input.onChange(newFinalizingHireSteps);
  };

  const addFinalizingHireStep = (step: Partial<JobFinalizingHireStep>) => () => {
    if (!step.step || !step.step.description || step.step.description === '') {
      return;
    }

    const stepId = step.step && step.step.stepId;
    const existingStepIndex = finalizingHireSteps.findIndex(
      (fhs) => fhs.jobId && fhs.stepId === stepId,
    );
    let updatedSteps;

    if (existingStepIndex !== -1) {
      updatedSteps = cloneDeep(finalizingHireSteps);
      updatedSteps[existingStepIndex].isActive = true;
    } else {
      updatedSteps = [...finalizingHireSteps, step];
    }

    onChange(updatedSteps);
  };

  const removeFinalizingHireStep = (step: Partial<JobFinalizingHireStep>) => () => {
    let updatedSteps;

    if (step.stepId) {
      const index = finalizingHireSteps.findIndex(
        (fhs) => fhs.step && fhs.step.stepId === step.stepId,
      );
      updatedSteps = cloneDeep(finalizingHireSteps);
      updatedSteps[index].isActive = false;
    } else {
      updatedSteps = finalizingHireSteps.filter(
        (fhs) => fhs.step.description !== step.step.description,
      );
    }

    onChange(updatedSteps);
  };

  const handleCustomStepDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomStepDescription(e.target.value);
  };

  const { commonFinalizingHireSteps } = props;
  const activeSteps = finalizingHireSteps.filter((step) => step.isActive);

  if (!commonFinalizingHireSteps) {
    return null;
  }
  const listedCommonSteps = commonFinalizingHireSteps.filter((commonStep) => {
    const existingStep = finalizingHireSteps.find(
      (step) => (step.step && step.step.stepId) === commonStep.stepId,
    );
    return !(existingStep && existingStep.isActive);
  });

  return (
    <>
      <Paper className="u-padding">
        {activeSteps.length === 0 ? (
          <div>Nothing for an applicant to do after the interview.</div>
        ) : (
          <>
            <div className="typo-title u-color-secondary">Current Pre-hire steps</div>
            <div>Before you hire an applicant, they will need...</div>
            {activeSteps.map((step) => (
              <div className="u-flex-center u-margin-top" key={step.step.description}>
                <MaterialDesignIcon
                  iconName={ICON_NAMES.CANCEL}
                  className="u-margin-right-xs"
                  onClick={removeFinalizingHireStep(step)}
                />
                {step.step.description}
              </div>
            ))}
          </>
        )}
      </Paper>
      <Accordion>
        <AccordionSummary expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}>
          <div className="typo-title">Add pre-hire steps</div>
        </AccordionSummary>
        <AccordionDetails>
          <div>
            {listedCommonSteps.map((step) => (
              <div className="u-flex-center u-margin-bottom" key={step.stepId}>
                <MaterialDesignIcon
                  iconName={ICON_NAMES.ADD_CIRCLE}
                  className="u-margin-right-xs"
                  onClick={addFinalizingHireStep({ isActive: true, step })}
                />
                {step.description}
              </div>
            ))}
            <div className="u-display-flex u-alignItems-flexEnd">
              <TextField
                id="customStepDescription"
                label="Custom step"
                value={customStepDescription}
                onChange={handleCustomStepDescriptionChange}
                margin="dense"
              />
              <UiButton
                className="u-margin-left"
                onClick={addFinalizingHireStep({
                  isActive: true,
                  step: { stepId: null, isActive: true, description: customStepDescription },
                })}
                size="sm"
              >
                Add
              </UiButton>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
